/**
 * Admin V3 — Corporate / Entreprises — Search / filter bar
 *
 * Layout (left → right):
 *   [Status filter] [Search input] [Result counter]
 *
 * Both controls are CLIENT-SIDE — the backend /api/enterprises endpoint
 * accepts no query params (enterprise.py:113-119). Filtering operates only
 * over the loaded page (max 100 records).
 *
 * NO CSV (no backend endpoint).
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { enterpriseStatusMeta } from '../../shared/visualTokens';
import { t } from '../../i18n';

const ALL_SENTINEL = '__all__';

const STATUS_VALUES = ['active', 'suspended', 'inactive'];

const CorporateListSearchBar = ({
  status,
  onStatusChange,
  search,
  onSearchChange,
  resultsCount,
  isFilterActive,
  loading,
  locale = 'fr',
}) => {
  const selectValue = status || ALL_SENTINEL;
  const handleStatus = (v) => onStatusChange(v === ALL_SENTINEL ? '' : v);

  const resultsCountText = t('corporate.results.count', locale).replace('{n}', String(resultsCount));

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Select value={selectValue} onValueChange={handleStatus}>
        <SelectTrigger
          className="h-10 w-[200px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-corporate-status-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SENTINEL}>{t('corporate.filter.statusAll', locale)}</SelectItem>
          {STATUS_VALUES.map((v) => (
            <SelectItem key={v} value={v}>{enterpriseStatusMeta[v]?.label || v}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="relative flex-1 max-w-md min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('corporate.search.placeholder', locale)}
          data-testid="adminv3-corporate-search"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="adminv3-corporate-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isFilterActive && !loading && (
        <span className="text-sm text-slate-400" data-testid="adminv3-corporate-results-count">
          {resultsCountText}
        </span>
      )}
    </div>
  );
};

export default CorporateListSearchBar;
